import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { useCreateClassroom, useUpdateClassroom } from './useClassroomOperations';

interface ClassroomValue {
  id: string;
  name: string;
  description?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  classroom?: ClassroomValue | null;
  onComplete: () => void | Promise<void>;
  onRefreshError?: (message: string) => void;
}

export default function ClassroomDialog({
  open,
  onOpenChange,
  schoolId,
  classroom,
  onComplete,
  onRefreshError,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createClassroom] = useCreateClassroom();
  const [updateClassroom] = useUpdateClassroom();

  useEffect(() => {
    if (!open) return;
    setName(classroom?.name ?? '');
    setDescription(classroom?.description ?? '');
    setError(null);
  }, [classroom, open]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);

    let errors: ReadonlyArray<{ message: string }> = [];
    try {
      if (classroom) {
        const result = await updateClassroom({
          variables: { input: { id: classroom.id, name: name.trim(), description: description.trim() } },
        });
        errors = result.data?.updateClassroom?.userErrors ?? [];
      } else {
        const result = await createClassroom({
          variables: { input: { schoolId, name: name.trim(), description: description.trim() } },
        });
        errors = result.data?.createClassroom?.userErrors ?? [];
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save this location.');
      setSaving(false);
      return;
    }

    if (errors.length > 0) {
      setError(errors.map((item) => item.message).join(', '));
      setSaving(false);
      return;
    }

    // The location mutation is committed. Close before refreshing so a failed
    // parent query cannot turn a successful create into a duplicate retry.
    onOpenChange(false);
    try {
      await onComplete();
    } catch (refreshError) {
      const detail = refreshError instanceof Error && refreshError.message ? `: ${refreshError.message}` : '';
      onRefreshError?.(`Location saved, but locations could not be refreshed${detail}. Reload the page to see the saved changes.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{classroom ? 'Edit room or location' : 'Add room or location'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
          <div className="space-y-2">
            <Label htmlFor="classroom-name">Name</Label>
            <Input
              id="classroom-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Room 205 or North Field"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="classroom-description">Description (optional)</Label>
            <Textarea
              id="classroom-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Science lab, second floor"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save location'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
